import {
    RecipeStructuredData,
    ArticleStructuredData,
    ArticleListStructuredData,
    FAQStructuredData,
    generateRecipeStructuredData,
    generateArticleStructuredData,
    generateWebsiteStructuredData,
    generateBreadcrumbStructuredData,
    generateArticleListStructuredData,
    generateFAQStructuredData
} from "@/lib/structuredData";

interface StructuredDataProps {
    type: 'recipe' | 'article' | 'article-list' | 'website' | 'breadcrumb' | 'faq';
    data?: RecipeStructuredData | ArticleStructuredData | ArticleListStructuredData | FAQStructuredData | Array<{ name: string, url: string }>;
}

/**
 * Component to inject structured data (JSON-LD) into the page head
 * Improves SEO by providing search engines with structured information
 */
export const StructuredData: React.FC<StructuredDataProps> = ({ type, data }) => {
    const getStructuredData = () => {
        switch (type) {
            case 'recipe':
                if (!data || !('title' in data) || !('ingredients' in data)) return null;
                return generateRecipeStructuredData(data as RecipeStructuredData);

            case 'article':
                if (!data || !('title' in data) || !('content' in data)) return null;
                return generateArticleStructuredData(data as ArticleStructuredData);

            case 'article-list':
                if (!data || !('items' in data)) return null;
                return generateArticleListStructuredData(data as ArticleListStructuredData);

            case 'breadcrumb':
                if (!data || !Array.isArray(data)) return null;
                return generateBreadcrumbStructuredData(data as Array<{ name: string, url: string }>);

            case 'website':
                return generateWebsiteStructuredData();

            case 'faq':
                if (!data || !('questions' in data)) return null;
                return generateFAQStructuredData(data as FAQStructuredData);

            default:
                return null;
        }
    };

    const structuredData = getStructuredData();

    if (!structuredData) return null;

    return (
        <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: structuredData }}
        />
    );
};

export default StructuredData;
